import React from "react";
import { cn } from "@/lib/utils";
import { ScoreRing, GRADE_DOT, GRADE_TEXT, GRADE_GRAD } from "@/components/ExecutionScore";

const LEAD_LABEL = {
  delegation_quality: "Kualitas Delegasi",
  escalation_response: "Respons Eskalasi",
  review_throughput: "Ketuntasan Tinjauan",
};

export function formatHoursID(h) {
  if (h == null) return "—";
  if (h < 1) return "<1 jam";
  if (h < 48) return `${Math.round(h)} jam`;
  return `${(h / 24).toFixed(1).replace(".", ",")} hari`;
}

export function LeadershipScoreCard({ data, title = "Skor Kepemimpinan" }) {
  if (!data) return null;
  const { score, grade, components, counts } = data;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="leadership-score-card">
      <div className="flex items-center gap-5">
        <ScoreRing score={score} grade={grade} size={92} />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{title}</p>
          <p className={cn("text-lg font-extrabold", GRADE_TEXT[grade?.color])}>{grade?.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            {counts.given} tugas diberikan · {counts.review_pending} tinjauan tertunda · respons eskalasi {formatHoursID(data.avg_escalation_response_hours)}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {Object.entries(components).map(([k, v]) => (
          <div key={k} data-testid={`leadership-comp-${k}`}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-slate-500">{LEAD_LABEL[k]}</span>
              <span className="font-bold text-slate-700">{v}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full bg-gradient-to-r", GRADE_GRAD[grade?.color])} style={{ width: `${v}%` }} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-400">
        <span data-testid="leadership-meta-delegation">{counts.on_time}/{counts.completed} selesai tepat waktu · {counts.revisions} revisi</span>
        <span data-testid="leadership-meta-escalation">{counts.escalated} eskalasi · {counts.escalation_pending} belum direspons</span>
        <span data-testid="leadership-meta-review">{counts.review_processed} tinjauan selesai · antre {formatHoursID(data.avg_review_wait_hours)}</span>
      </div>
    </div>
  );
}

export function LeadershipRankList({ leaders = [], meId, title = "Peringkat Kepemimpinan" }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm h-full" data-testid="leadership-rank-list">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">{title}</p>
      <div className="space-y-2">
        {leaders.map((l) => (
          <div key={l.user_id} className="flex items-center gap-3" data-testid={`leader-row-${l.user_id}`}>
            <span className="text-sm text-slate-700 flex-1 truncate">
              {l.name}{l.user_id === meId && <span className="text-xs text-indigo-500 font-semibold"> (Anda)</span>}
            </span>
            <span className="text-[11px] text-slate-400 hidden md:block">
              delegasi {l.components.delegation_quality}% · eskalasi {l.components.escalation_response}% · tinjauan {l.components.review_throughput}%
            </span>
            <div className="h-1.5 w-24 rounded-full bg-slate-100 overflow-hidden">
              <div className={cn("h-full rounded-full", GRADE_DOT[l.grade.color])} style={{ width: `${l.score}%` }} />
            </div>
            <span className={cn("text-sm font-bold w-8 text-right", GRADE_TEXT[l.grade.color])}>{l.score}</span>
          </div>
        ))}
        {leaders.length === 0 && <p className="text-sm text-slate-400 italic">Belum ada data atasan.</p>}
      </div>
    </div>
  );
}
